import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, TasksStore, toArray, UserAgent } from 'Mri.Web.Common';
import * as React from 'react';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { NocServersStore } from '../NocServers/NocServersStore';
import { AppStore } from './AppStore';

export interface IRdpLinkProps {
    readonly className?: string;
    readonly children: React.ReactNode;
    readonly display: Mri.Displays.Display;
    readonly displayLogin?: Mri.DisplaySystem.DisplayLoginPoll;
    readonly type: 'chrome' | 'input1' | 'input2' | 'camera' | 'putty' | 'sigmaCenter';
    readonly appStore?: AppStore;
    readonly nocServersStore?: NocServersStore;
}

@autoinject
@observer
export class RdpLink extends React.Component<IRdpLinkProps> {
    public async componentDidMount() {
        try {
            await this.nocServersStore.loadAllAsync(true);
        } catch (e: any) {
            console.error(e);
        }
    }

    public render() {
        if (!this.url) return null;

        return (
            <a className={this.props.className} href={this.url} onClick={this.onClick}>
                {this.props.children}
            </a>
        );
    }

    @bind
    private async onClick(event: React.MouseEvent<HTMLAnchorElement>) {
        if (this.props.type != 'sigmaCenter') return;

        event.preventDefault();

        try {
            await this.displaysStore.createProxyAsync(this.props.display.serialNumber, 'sigma');

            if (this.url) window.location.href = this.url;
        } catch (e: any) {
            this.tasksStore.toastErrorTask('Proxy Error', e);
        }
    }

    @computed
    private get url() {
        const d = this.props.display;
        const dcd = d.displayConnectionData;
        const dl = this.props.displayLogin;
        const nocServers = toArray(this.nocServersStore.nocServers);
        const type = this.props.type;
        let url = '';
        let args = '';
        let program = '';

        if (!dcd) return;

        const nocServer = nocServers.find(n => n.serverName == dcd.nocServer);

        if (!nocServer) return;

        switch (type) {
            case 'chrome':
                if (!dcd.displayBoardIP) return;
                break;
            case 'input1':
                if (!dcd.input1IP || !dcd.input1Password) return;
                break;
            case 'input2':
                if (!dcd.input2IP || !dcd.input2Password) return;
                break;
            case 'camera':
                if (!dcd.cameraIP) return;
                break;
            case 'putty':
                if (!dcd.displayBoardIP) return;
                break;
        }

        switch (type) {
            case 'chrome':
                program = 'chrome';
                let link = dcd.displayBoardIP;

                if (dcd.displayBoardWebsitePort) link = link + ':' + dcd.displayBoardWebsitePort;

                if (dl?.username && dl?.password) link = dl.username + ':' + dl.password + '@' + link;

                args = `--no-sandbox --disable-infobars --disable-gpu --disable-gpu-sandbox --ignore-certificate-errors https://${link}`;
                break;
            case 'input1':
                program = 'vncviewer';
                args = `${dcd.input1IP} /password ${dcd.input1Password} /quickoption 4`;
                break;
            case 'input2':
                program = 'vncviewer';
                args = `${dcd.input2IP} /password ${dcd.input2Password} /quickoption 4`;
                break;
            case 'camera':
                program = 'chrome';
                args = `--allow-no-sandbox-job --disable-gpu http://${dcd.cameraIP}`;
                break;
            case 'sigmaCenter':
                program = 'SigmaCenter';
                args = '';
                break;
            case 'putty':
                program = 'putty';
                args = `-ssh ${dcd.displayBoardIP}`;

                if (dcd.sslPort) args += ' ' + dcd.sslPort;

                break;
        }

        if (UserAgent.android() || UserAgent.appleios()) {
            url = `rdp://remoteapplicationmode=i:1&
                authentication level=i:2&
                full address=s:${nocServer.remoteAppUri}:3389&
                alternate shell=s:${program}&
                remoteapplicationmode=i:1&
                remoteapplicationprogram=s:${program}&
                remoteapplicationcmdline=s:${args}`;

            url = url.replace(/^\s/gm, ''); // remove leading whitespaces
            url = url.replace(/\n/gm, ''); // remove newlines
        } else if (UserAgent.linux()) {
            url = `rdp:///v:${nocServer.remoteAppUri} +clipboard /app:"||${program}" /app-cmd:"${args}"`;
        } else if (UserAgent.windows()) {
            url = `rdp://redirectclipboard:i:1
                redirectposdevices:i:0
                redireetprinters:i:1
                redirectcomports:i:1
                edirectsmartcards:i:1
                devicestoredirect:s:*
                drivestoredirect:s:*
                redirectdrives:i:1
                remoteapplicationmode:i:1
                server port:i:3389
                authentication level:i:2
                full address:s:${nocServer.remoteAppUri}
                alternate shell:s:%7C%7C${program}
                remoteapplicationprogram:s:%7C%7C${program}
                remoteapplicationname:s:NOC_UI
                remoteapplicationcmdline:s:${args}
                `; // Must end in newline?! WHY THO!

            url = url.replace(/^\s*/gm, ''); // remove leading whitespaces
            url = url.replace(/\n/gm, '%0A'); // replace newlines
            url = url.replace(/\s/g, '%20'); // replace whitespaces
        }

        return url;
    }

    @inject
    private appStore!: AppStore;
    @inject
    private nocServersStore!: NocServersStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}
